import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@/features/auth/store";
import {
  useUpdateSettings,
  useTestEmail,
} from "@/features/settings/hooks/useSettingsMutations";
import {
  emailSettingsValidation,
  testEmailValidation,
  type EmailSettingsFormValues,
  type TestEmailFormValues,
} from "@/validations/settings";

interface EmailSettingsFormProps {
  emailFrom?: string;
}

export function EmailSettingsForm({ emailFrom }: EmailSettingsFormProps) {
  const user = useUser();
  const updateMutation = useUpdateSettings();
  const testEmailMutation = useTestEmail();
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    detail: string;
  } | null>(null);

  const emailFormMethods = useForm<EmailSettingsFormValues>({
    values: { emailFrom: emailFrom ?? "" },
    resolver: zodResolver(emailSettingsValidation),
  });

  const testFormMethods = useForm<TestEmailFormValues>({
    values: { to: user?.email ?? "" },
    resolver: zodResolver(testEmailValidation),
  });

  const isPending = emailFormMethods.formState.isSubmitting || updateMutation.isPending;

  async function onSubmitEmailFrom(data: EmailSettingsFormValues) {
    await updateMutation.mutateAsync({ emailFrom: data.emailFrom });
  }

  async function onSubmitTestEmail(data: TestEmailFormValues) {
    setTestResult(null);
    const result = await testEmailMutation.mutateAsync(data);
    setTestResult({ ok: result.ok, detail: result.detail });
  }

  return (
    <div className="space-y-6">
      <Form {...emailFormMethods}>
        <form
          onSubmit={emailFormMethods.handleSubmit(onSubmitEmailFrom)}
          className="space-y-4"
        >
          <FormField
            control={emailFormMethods.control}
            name="emailFrom"
            render={({ field }) => (
              <FormItem>
                <FormLabel>عنوان المرسل (From)</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="no-reply@mof.gov.iq"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isPending}>
            حفظ إعدادات البريد الإلكتروني
            {isPending && <Loader className="mr-2" />}
          </Button>
        </form>
      </Form>

      <Separator />

      <Form {...testFormMethods}>
        <form
          onSubmit={testFormMethods.handleSubmit(onSubmitTestEmail)}
          className="space-y-4"
        >
          <FormField
            control={testFormMethods.control}
            name="to"
            render={({ field }) => (
              <FormItem>
                <FormLabel>إرسال بريد تجريبي إلى</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@mof.gov.iq" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            variant="secondary"
            disabled={testEmailMutation.isPending}
          >
            إرسال بريد تجريبي
            {testEmailMutation.isPending && <Loader className="mr-2" />}
          </Button>

          {testResult && (
            <p
              className={
                testResult.ok
                  ? "text-sm text-emerald-600"
                  : "text-sm text-destructive"
              }
            >
              {testResult.ok
                ? "تم إرسال البريد التجريبي بنجاح."
                : `فشل إرسال البريد: ${testResult.detail}`}
            </p>
          )}
        </form>
      </Form>
    </div>
  );
}
